import { Button, Typography } from '@carto/meridian-ds/components'
import { alpha, Box, styled } from '@mui/material'
import { Connection, OAuthSnowflakeState, ProviderType, setCreateConnectionError } from 'features/connections'
import { useForm } from 'react-hook-form'
import workspaceVectorUrl from 'assets/img/carto-logo-positive.svg'
import DocLink from 'features/common/ui/utils/DocLink'
import { FormattedMessage } from 'react-intl'
import { PowerOutlined } from '@mui/icons-material'
import { useAppDispatch } from 'app/state'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSaveConnection } from 'features/connections/api/connectionsQueries'
import { prepareConnectionPayload } from 'features/connections/ui/ConnectionForm/utils'
import SnowflakeConnectionWithOAuthForm from 'features/connections/ui/ConnectionForm/DBConnectionForm/SnowflakeConnection/SnowflakeConnectionWithOAuthForm'

const Form = styled('form')(() => ({
  display: 'flex',
  height: '100%',
  width: '100%'
}))

const FormPanel = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: theme.palette.common.white,
  borderRadius: theme.spacing(0, 1, 1, 0)
}))

const HelpPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(5, 4),
  width: theme.spacing(40),
  backgroundColor: alpha(theme.palette.grey[50], 0.4),
  flexShrink: 0,
  borderRadius: theme.spacing(1, 0, 0, 1),
  borderRight: `1px solid ${theme.palette.divider}`
}))
function GettingStartedForm({
  selectedRole,
  oauthState,
  defaultExternalIdp,
  onRequestNewOauth: handleOAuth,
  onCancel: handleCancel
}) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { mutateAsync: saveConnection } = useSaveConnection()

  const { handleSubmit, ...form } = useForm({
    mode: 'onChange'
  })

  const submit = async (data: unknown) => {
    const conn = prepareConnectionPayload({
      data,
      provider: ProviderType.SNOWFLAKE
    }) as Connection
    await saveConnection(conn)
    navigate('/')
  }

  useEffect(() => {
    return () => {
      dispatch(setCreateConnectionError(null))
    }
  }, [dispatch])

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <HelpPanel>
        <img src={workspaceVectorUrl} height={32} />
        <Typography variant="subtitle2" mt={4}>
          Need help?
        </Typography>
        <Typography variant="caption" my={2} component="p">
          Please check our documentation if you are having trouble connecting to your data
        </Typography>
        <DocLink variant="caption" href="#">
          <FormattedMessage id="common.button.viewDocumentation" />
        </DocLink>
      </HelpPanel>
      <FormPanel>
        <Box overflow="auto" height={(theme) => `calc(100% - ${theme.spacing(8)})`} px={10}>
          <Box mt={13} mr={1} mb={2}>
            <SnowflakeConnectionWithOAuthForm
              form={form}
              selectedRole={selectedRole}
              oauthState={oauthState as OAuthSnowflakeState}
              externalIdp={defaultExternalIdp}
              onRequestNewOauth={handleOAuth}
              hideAdvanced
            />
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          position="absolute"
          bottom={0}
          width="100%"
          p={2}
          borderTop={(theme) => `1px solid ${theme.palette.divider}`}
        >
          <Button onClick={handleCancel}>
            <FormattedMessage id="common.button.cancel" />
          </Button>
          <Button startIcon={<PowerOutlined />} variant="contained" type="submit">
            <FormattedMessage id="connections.create.common.connect" />
          </Button>
        </Box>
      </FormPanel>
    </Form>
  )
}

export default GettingStartedForm
